import React, { createContext, useContext, useEffect, useState } from 'react';
import {
  CookieConsentPreferences,
  CookieConsentState,
  COOKIE_CONSENT_STORAGE_KEY,
  DEFAULT_PREFERENCES,
  ACCEPTED_ALL_PREFERENCES,
  REJECTED_ALL_PREFERENCES,
} from '../types/consent';
import {
  setDefaultConsent,
  enableGoogleAnalytics,
  disableGoogleAnalytics,
} from '../services/analytics';
import { DOMAIN_CONFIG } from '../config';

interface ConsentContextType {
  state: CookieConsentState;
  isBannerVisible: boolean;
  isSettingsOpen: boolean;
  openSettings: () => void;
  closeSettings: () => void;
  acceptAll: () => void;
  rejectAll: () => void;
  saveCustomPreferences: (prefs: Partial<CookieConsentPreferences>) => void;
}

const ConsentContext = createContext<ConsentContextType | undefined>(undefined);

export const ConsentProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [state, setState] = useState<CookieConsentState>(() => {
    // Attempt to hydrate consent from localStorage
    if (typeof window !== 'undefined') {
      try {
        const stored = localStorage.getItem(COOKIE_CONSENT_STORAGE_KEY);
        if (stored) {
          const parsed = JSON.parse(stored);
          if (parsed && typeof parsed.hasInteracted === 'boolean' && parsed.preferences) {
            return {
              hasInteracted: parsed.hasInteracted,
              preferences: {
                necessary: true,
                analytics: Boolean(parsed.preferences.analytics),
                preferences: Boolean(parsed.preferences.preferences),
              },
              timestamp: parsed.timestamp || null,
            };
          }
        }
      } catch (err) {
        console.warn('Failed to parse stored cookie consent:', err);
      }
    }
    return {
      hasInteracted: false,
      preferences: DEFAULT_PREFERENCES,
      timestamp: null,
    };
  });

  const [isSettingsOpen, setIsSettingsOpen] = useState(false);

  // Initialize Consent Mode v2 and apply current preference on mount / change
  useEffect(() => {
    setDefaultConsent();

    if (state.hasInteracted) {
      if (state.preferences.analytics && DOMAIN_CONFIG.gaMeasurementId) {
        enableGoogleAnalytics(DOMAIN_CONFIG.gaMeasurementId);
      } else if (DOMAIN_CONFIG.gaMeasurementId) {
        disableGoogleAnalytics(DOMAIN_CONFIG.gaMeasurementId);
      }
    }
  }, [state.hasInteracted, state.preferences.analytics]);

  const saveToStorage = (newState: CookieConsentState) => {
    setState(newState);
    try {
      localStorage.setItem(COOKIE_CONSENT_STORAGE_KEY, JSON.stringify(newState));
    } catch (err) {
      console.warn('Failed to persist cookie consent to localStorage:', err);
    }
  };

  const acceptAll = () => {
    const newState: CookieConsentState = {
      hasInteracted: true,
      preferences: ACCEPTED_ALL_PREFERENCES,
      timestamp: new Date().toISOString(),
    };
    saveToStorage(newState);
    setIsSettingsOpen(false);
  };

  const rejectAll = () => {
    const newState: CookieConsentState = {
      hasInteracted: true,
      preferences: REJECTED_ALL_PREFERENCES,
      timestamp: new Date().toISOString(),
    };
    saveToStorage(newState);
    setIsSettingsOpen(false);
  };

  const saveCustomPreferences = (prefs: Partial<CookieConsentPreferences>) => {
    const newState: CookieConsentState = {
      hasInteracted: true,
      preferences: {
        necessary: true, // Always required
        analytics: Boolean(prefs.analytics),
        preferences: Boolean(prefs.preferences),
      },
      timestamp: new Date().toISOString(),
    };
    saveToStorage(newState);
    setIsSettingsOpen(false);
  };

  const openSettings = () => setIsSettingsOpen(true);
  const closeSettings = () => {
    // Only allow closing settings without saving if user has already interacted previously
    if (state.hasInteracted) {
      setIsSettingsOpen(false);
    }
  };

  return (
    <ConsentContext.Provider
      value={{
        state,
        isBannerVisible: !state.hasInteracted,
        isSettingsOpen,
        openSettings,
        closeSettings,
        acceptAll,
        rejectAll,
        saveCustomPreferences,
      }}
    >
      {children}
    </ConsentContext.Provider>
  );
};

export const useConsent = (): ConsentContextType => {
  const context = useContext(ConsentContext);
  if (!context) {
    throw new Error('useConsent must be used within a ConsentProvider');
  }
  return context;
};
